import {
  Badge,
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import firebase from 'firebase/app';

import { useCallback, useMemo, useState } from 'react';
import { useFirestore, useFirestoreConnect } from 'react-redux-firebase';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import {
  NOT_APPLICABLE,
  RIGHT_ALIGN_BODY,
  RIGHT_ALIGN_HEADER,
  UNSPECIFIED_LABEL,
} from '../../../../config/constants';
import { AppState, useAppDispatch } from '../../../../config/store';
import useSurveysMeta, {
  parseSurveysMeta,
} from '../../../../hooks/useSurveysMeta';
import {
  autoSizeWorksheetWidth,
  dateFromTimeStamp,
  getCleanedRoomScheduleData,
  getSafeExcelSheetName,
  getStatusColour,
  getStyledExportHeaders,
  getStyledRows,
  handlePlural,
  statusToString,
  statuses,
  timeFromTimeStamp,
} from '../../../../shared/utilities';

import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import { useSelector } from 'react-redux';
import XLSX from 'xlsx-js-style';
import ErrorBoundary from '../../../../components/ErrorBoundary';
import LastUpdatedTableCell from '../../../../components/LastUpdatedTableCell';
import { useModal } from '../../../../components/Modal';
import StatusStringTableCell from '../../../../components/StatusStringTableCell';
import Table from '../../../../components/Table';
import TableActionButton from '../../../../components/TableActionButton';
import TagsSelectorDialog from '../../../../components/TagsSelectorDialog';
import TagsTableCell from '../../../../components/TagsTableCell';
import { ALL_ROLES, PORTAL_ROLES } from '../../../../config/roles';
import useAuthChecker from '../../../../hooks/useAuthChecker';
import { TagsSelectorProvider } from '../../../../hooks/useTagsSelector';
import {
  canShowSurveySetupEditAction,
  canViewMultipleSurveyors,
} from '../../../../shared/auth';
import { getLastUpdated } from '../../../../shared/logTools';
import {
  FORM_CONFIG_DOC_ID,
  resolveSurveyFormConfig,
} from '../../../../state/form-config';
import {
  FLOOR_PLANS_CONFIG_DOC_ID,
  floorPlansArrayToConfigMap,
  queueFloorPlansConfigDocWrite,
  resolveSurveyFloorPlansConfig,
} from '../../../../state/floorplans-config';
import { onSelectedSurveyDelete } from '../../../../state/survey';
import { MissingUploads } from '../../../../types/missing-uploads';
import { SiteProps } from '../../../../types/site';
import { Statuses } from '../../../../types/status';
import { SurveyProps, type SurveyTag } from '../../../../types/survey';
import { UrlParams } from '../../../../types/urlParams';
import {
  getAdditionalSurveyorEntries,
  getSurveyorSearchText,
  type SurveyorAssignmentByUid,
} from '../../../../shared/surveyorDisplay';

const SurveyorNamesTableCell = ({ cell, showAdditionalSurveyors }) => {
  const mainSurveyorId = cell.row.original.surveyorId;
  const surveyors: SurveyorAssignmentByUid = cell.row.original.surveyors || {};

  const mainSurveyor = surveyors?.[mainSurveyorId];

  const additionalSurveyors = getAdditionalSurveyorEntries({
    surveyors,
    mainSurveyorId,
  });

  const hasAdditionalSurveyors =
    showAdditionalSurveyors && additionalSurveyors?.length > 0;
  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Typography>{cell.getValue()}</Typography>
      {hasAdditionalSurveyors ? (
        <Tooltip
          title={
            <Stack spacing={0.5}>
              <Typography variant="body2" fontWeight="bold">
                Main Surveyor
              </Typography>
              {mainSurveyor ? (
                <Typography variant="body2">
                  {mainSurveyor.name} -{' '}
                  <span style={{ color: getStatusColour(mainSurveyor.status) }}>
                    {' '}
                    {statusToString(mainSurveyor.status)}{' '}
                  </span>{' '}
                </Typography>
              ) : (
                <Typography variant="body2">{cell.getValue()}</Typography>
              )}
              <Typography variant="body2" fontWeight="bold">
                Additional Surveyors
              </Typography>
              {additionalSurveyors?.map(([uid, surveyor]) => {
                return (
                  <Typography key={uid} variant="body2">
                    {surveyor.name} -{' '}
                    <span style={{ color: getStatusColour(surveyor.status) }}>
                      {' '}
                      {statusToString(surveyor.status)}{' '}
                    </span>{' '}
                  </Typography>
                );
              })}{' '}
            </Stack>
          }
        >
          <Box paddingRight={1}>
            <Badge badgeContent={additionalSurveyors?.length} color="primary">
              <PersonAddIcon color="action" />
            </Badge>
          </Box>
        </Tooltip>
      ) : null}
    </Stack>
  );
};

const SurveyNameTableCell = ({ cell, onClickToolTip }) => {
  const name = cell.getValue();
  const uploadsNotFound = cell.row.original.uploadsNotFound;
  const uploadsOnOtherDevice = cell.row.original.uploadsOnOtherDevice;
  const uploadsLost = cell.row.original.uploadsLost;
  const totalMissing = uploadsOnOtherDevice + uploadsNotFound + uploadsLost;

  return (
    <>
      <Stack alignItems="center" direction="row">
        {name}
        {uploadsOnOtherDevice || uploadsLost || uploadsNotFound ? (
          <Tooltip
            title={
              <Typography>
                This survey has {totalMissing} missing{' '}
                {handlePlural(totalMissing, 'uploads', false)}
                <br />
                Click to see more details
              </Typography>
            }
            sx={{ width: 200 }}
          >
            <IconButton
              aria-label="delete"
              sx={{ ml: 0.5 }}
              onClick={(e) => {
                e.stopPropagation();
                onClickToolTip?.(cell.row.original);
              }}
            >
              <WarningAmberIcon color="error" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
    </>
  );
};

type SurveyHistory = {
  time: number;
  status: string;
};

function parseSurveyHistories(histories) {
  return (
    histories
      .map((survey) => {
        const key = Object.keys(survey)[0];
        return { [key]: survey[key] };
      })
      //remove any empties
      .filter((s) => s)
  );
}

const OnOtherDevice = ({ uploadsOnOtherDevice }) => {
  const list = useMemo(() => {
    return uploadsOnOtherDevice.map((upload) => {
      return (
        <Box
          sx={{
            border: 1,
            p: 1,
            borderColor: 'primary.main',
            borderRadius: 1,
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
          key={upload.fileId}
        >
          <Typography variant="caption">{`Description: ${upload.description}`}</Typography>
          {upload.lastUpdated ? (
            <>
              <Typography variant="caption">
                {`Created: ${dateFromTimeStamp(
                  upload.lastUpdated.time,
                )} at ${timeFromTimeStamp(upload.lastUpdated.time)}`}
              </Typography>

              <Typography variant="caption">
                {`Location: ${upload.lastUpdated.name}'s ${upload.lastUpdated.manufacturer} ${upload.lastUpdated.deviceName}`}
              </Typography>
            </>
          ) : null}
        </Box>
      );
    });
  }, [uploadsOnOtherDevice]);

  return list.length ? (
    <Stack my={1} border={1} p={2} borderRadius={2} borderColor="primary.main">
      <Typography color="primary.main" fontWeight={600}>
        Uploads on other devices
      </Typography>
      <Typography color="secondary.main" variant="body2" mb={1}>
        In order to retrieve these uploads, you should reissue the survey to the
        relevant surveyor and ask them to complete the survey from the device
        specified.
      </Typography>
      {list}
    </Stack>
  ) : null;
};

const NotFoundOnCurrentDevice = ({ uploadsNotFound }) => {
  const notFoundList = useMemo(() => {
    return uploadsNotFound
      .filter((u) => typeof u.lastUpdated === 'undefined')
      .map((upload) => {
        return (
          <Box
            sx={{
              border: 1,
              p: 1,
              borderColor: 'primary.main',
              borderRadius: 1,
              my: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
            key={upload.fileId}
          >
            <Typography variant="caption">{`Description: ${upload.description}`}</Typography>
            {upload.lastUpdated ? (
              <>
                <Typography variant="caption">
                  {`Created: ${dateFromTimeStamp(
                    upload.lastUpdated.time,
                  )} at ${timeFromTimeStamp(upload.lastUpdated.time)}`}
                </Typography>

                <Typography variant="caption">
                  {`Location: ${upload.lastUpdated.name}'s ${upload.lastUpdated.manufacturer} ${upload.lastUpdated.deviceName}`}
                </Typography>
              </>
            ) : null}
          </Box>
        );
      });
  }, [uploadsNotFound]);

  const otherDevicesList = useMemo(() => {
    return uploadsNotFound
      .filter((u) => typeof u.lastUpdated !== 'undefined')
      .map((upload) => {
        return (
          <Box
            sx={{
              border: 1,
              p: 1,
              borderColor: 'primary.main',
              borderRadius: 1,
              my: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
            }}
            key={upload.fileId}
          >
            <Typography variant="caption">{`Description: ${upload.description}`}</Typography>
            {upload.lastUpdated ? (
              <>
                <Typography variant="caption">
                  {`Created: ${dateFromTimeStamp(
                    upload.lastUpdated.time,
                  )} at ${timeFromTimeStamp(upload.lastUpdated.time)}`}
                </Typography>

                <Typography variant="caption">
                  {`Location: ${upload.lastUpdated.name}'s ${upload.lastUpdated.manufacturer} ${upload.lastUpdated.deviceName}`}
                </Typography>
              </>
            ) : null}
          </Box>
        );
      });
  }, [uploadsNotFound]);

  return otherDevicesList.length || notFoundList.length ? (
    <Stack my={1} border={1} p={2} borderRadius={2} borderColor="primary.main">
      {otherDevicesList.length ? (
        <Box>
          <Typography color="primary.main">
            Uploads that appear be lost
          </Typography>
          {otherDevicesList}
        </Box>
      ) : null}
      {notFoundList.length ? (
        <Stack>
          <Typography color="primary.main" fontWeight={600}>
            Uploads that may be on other devices
          </Typography>
          <Typography color="secondary.main" variant="body2" mb={1}>
            The location of these files could not be determined. They may be
            present on another device that was used to carry out this survey.
          </Typography>
          {notFoundList}
        </Stack>
      ) : null}
    </Stack>
  ) : null;
};

const LostFiles = ({ lostUploads }) => {
  const list = useMemo(() => {
    return lostUploads.map((upload) => {
      return (
        <Box
          sx={{
            border: 1,
            p: 1,
            borderColor: 'primary.main',
            borderRadius: 1,
            my: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
          key={upload.fileId}
        >
          <Typography variant="caption">
            {`Created: ${dateFromTimeStamp(
              upload.lastUpdated.time,
            )} at ${timeFromTimeStamp(upload.lastUpdated.time)}`}
          </Typography>
          <Typography variant="caption">{`Description: ${upload.description}`}</Typography>
          <Typography variant="caption">
            {`Location: ${upload.lastUpdated.name}'s ${upload.lastUpdated.manufacturer} ${upload.lastUpdated.deviceName}`}
          </Typography>
        </Box>
      );
    });
  }, [lostUploads]);

  return lostUploads.length ? (
    <Stack my={1} border={1} p={2} borderRadius={2} borderColor="primary.main">
      <Typography color="primary.main" fontWeight={600}>
        Uploads that are not recoverable
      </Typography>
      <Typography color="secondary.main" variant="body2" mb={1}>
        When trying to upload, these files were not found on the device that
        created them. It may be that the app was uninstalled before the upload
        was attempted.
      </Typography>
      {list}
    </Stack>
  ) : null;
};

const MissingUploadsContent = ({
  clientId,
  surveyId,
}: {
  clientId: string | undefined;
  surveyId: string | undefined;
}) => {
  const missingUploads = useSelector(
    (state: AppState) => state.firestore.data.missingUploads,
  );

  const missingUploadsQuery = {
    collection: 'clients',
    doc: clientId,
    subcollections: [{ collection: 'missing-uploads', doc: surveyId }],
    storeAs: 'missingUploads',
  };

  useFirestoreConnect(clientId && surveyId ? [missingUploadsQuery] : []);

  return missingUploads ? (
    <>
      <OnOtherDevice
        uploadsOnOtherDevice={missingUploads.uploadsOnOtherDevice}
      />
      <NotFoundOnCurrentDevice
        uploadsNotFound={missingUploads.uploadsNotFound}
      />
      <LostFiles lostUploads={missingUploads.uploadsLost} />
    </>
  ) : null;

  // return (
  //   <OnOtherDevice uploadsOnOtherDevice={missingUploads.uploadsOnOtherDevice} />
  // );
};

const View = () => {
  const location = useLocation();
  const history = useHistory();
  const { clientId } = useParams<UrlParams>();
  const { showModal } = useModal();
  const firestore = useFirestore();
  const dispatch = useAppDispatch();

  const { isAuthed: canEditSurveys } = useAuthChecker({
    atLeast: PORTAL_ROLES.LEAD_SURVEYOR,
  });

  const [missingUploadsData, setMissingUploadsData] = useState<
    MissingUploads | undefined
  >(undefined);

  const userClaims = useSelector(
    (state: AppState) => state.firebase.profile.token?.claims,
  );

  const IS_VIEWER =
    clientId && userClaims?.roles?.[clientId] === ALL_ROLES.VIEWER;

  const IS_QUALITY_ASSURANCE =
    clientId && userClaims?.roles?.[clientId] === ALL_ROLES.QUALITY_ASSURANCE;

  const IS_ASSETICOM_ADMIN =
    userClaims?.roles?.['asseticom'] >= ALL_ROLES.ASSETICOM_ADMIN;
  const CAN_VIEW_MULTIPLE_SURVEYORS = canViewMultipleSurveyors({
    roles: userClaims?.roles,
    clientId,
  });

  const { surveys } = useSurveysMeta();

  const client = useSelector((state: AppState) => state.firestore.data.client);

  const markSurveyAsComplete = async (survey: SurveyProps) => {
    await firestore
      .collection('clients')
      .doc(clientId)
      .collection('surveys')
      .doc(survey.id)
      .update({ status: Statuses.COMPLETED, ...getLastUpdated() });
  };

  const showConfirmDialogForSurvey = async (survey) => {
    const messages = [
      `Are you sure you want to delete ${survey.name}?`,
      '*WARNING* You will lose access to all data associated with this survey',
    ];

    const deleteConfirmed = await showModal({
      title: 'Please Confirm',
      buttons: {
        confirm: [{ text: 'Yes', value: true }],
        cancel: [{ text: 'No' }],
      },
      messages,
    });
    if (deleteConfirmed) {
      dispatch(onSelectedSurveyDelete({ survey }));
    }
  };

  const getDuplicateOfSurvey = (survey) => {
    const surveyData = { ...survey };

    surveyData.assetsNumCreated = 0;
    surveyData.assetsNumDeleted = 0;
    surveyData.assetsNumFlagged = 0;
    surveyData.assetsNumImported = 0;
    surveyData.partialsNumCreated = 0;
    surveyData.partialsNumDeleted = 0;
    surveyData.roomsNumCompleted = 0;
    surveyData.roomsNumDeleted = 0;
    surveyData.roomsNumInProgress = 0;
    surveyData.roomsNumNotStarted = surveyData.roomSchedule?.data?.length || 0;
    surveyData.roomsNumDeleted = 0;
    surveyData.created = Date.now();
    surveyData.synced = 0;
    surveyData.roomSchedule = getCleanedRoomScheduleData(
      surveyData.roomSchedule,
    );
    surveyData.uploadsLost = 0;
    surveyData.uploadsNotFound = 0;
    surveyData.uploadsOnOtherDevice = 0;
    surveyData.uploadsQueueSize = 0;
    surveyData.status = Statuses.ISSUED;
    surveyData.surveyorName = 'Not Assigned';
    surveyData.surveyorId = UNSPECIFIED_LABEL;
    surveyData.surveyorEmail = UNSPECIFIED_LABEL;
    surveyData.surveyorIds = [];
    surveyData.surveyors = {};
    surveyData.formFieldsVersion = 0;
    surveyData.floorPlansVersion = 0;
    surveyData.surveyTags = [];

    delete surveyData.id;
    delete surveyData.assets;
    delete surveyData.partials;
    delete surveyData.flagged;
    delete surveyData.lastUpdated;

    return surveyData;
  };

  const onClickDuplicateSurvey = async (id) => {
    const surveyDoc = await firestore
      .collection('clients')
      .doc(clientId)
      .collection('surveys')
      .doc(id)
      .get();
    const sourceSurveyData = surveyDoc.data();
    const sourceFormConfigDoc = await firestore
      .collection('clients')
      .doc(clientId)
      .collection('surveys')
      .doc(id)
      .collection('config')
      .doc(FORM_CONFIG_DOC_ID)
      .get();
    const sourceFloorPlansConfigDoc = await firestore
      .collection('clients')
      .doc(clientId)
      .collection('surveys')
      .doc(id)
      .collection('config')
      .doc(FLOOR_PLANS_CONFIG_DOC_ID)
      .get();
    const { formFields, formOptions } = resolveSurveyFormConfig({
      surveyDocData: sourceSurveyData,
      formConfigData: sourceFormConfigDoc.exists
        ? sourceFormConfigDoc.data()
        : {},
      context: 'duplicate-survey',
      surveyId: id,
    });
    const { floorPlans } = resolveSurveyFloorPlansConfig({
      surveyDocData: sourceSurveyData,
      floorPlansConfigData: sourceFloorPlansConfigDoc.exists
        ? sourceFloorPlansConfigDoc.data()
        : {},
      context: 'duplicate-survey',
      surveyId: id,
    });
    const surveyData = getDuplicateOfSurvey(sourceSurveyData);
    const formOptionsForWrite =
      formOptions || sourceSurveyData?.formOptions || {};

    surveyData.duplicatedSurveyId = id;
    surveyData.name = ``;
    surveyData.formFieldsVersion = 0;
    surveyData.floorPlansVersion = 0;
    surveyData.floorPlans = floorPlans;
    surveyData.formFields = formFields;
    surveyData.formOptions = formOptionsForWrite;

    const newSurveyDoc = firestore
      .collection('clients')
      .doc(surveyData.clientId)
      .collection('surveys')
      .doc();
    const newFormConfigDoc = newSurveyDoc
      .collection('config')
      .doc(FORM_CONFIG_DOC_ID);
    const newFloorPlansConfigDoc = newSurveyDoc
      .collection('config')
      .doc(FLOOR_PLANS_CONFIG_DOC_ID);

    surveyData.id = newSurveyDoc.id;
    const duplicatedLastUpdated = getLastUpdated();
    const batch = firestore.batch();
    batch.set(newSurveyDoc, { ...surveyData, ...duplicatedLastUpdated });
    batch.set(
      newFormConfigDoc,
      {
        formFields,
        formOptions: formOptionsForWrite,
        ...duplicatedLastUpdated,
      },
      { merge: true },
    );
    queueFloorPlansConfigDocWrite({
      batch,
      floorPlansConfigDocRef: newFloorPlansConfigDoc,
      floorPlansByFloor: floorPlansArrayToConfigMap(floorPlans),
      lastUpdated: duplicatedLastUpdated,
    });
    await batch.commit();

    const surveyUri = `/${surveyData.clientId}/surveys/${newSurveyDoc.id}`;

    history.push(surveyUri);
  };

  const onClickDownloadSurvey = async ({ id }) => {
    const surveyDoc = await firestore
      .collection('clients')
      .doc(clientId)
      .collection('surveys')
      .doc(id)
      .get();

    const surveyData = getDuplicateOfSurvey(surveyDoc.data());
    surveyData.name = `Export of ${surveyData.name}`;
    const dataStr =
      'data:text/json;charset=utf-8,' +
      encodeURIComponent(JSON.stringify(surveyData, null, 2));
    const downloadAnchorNode = document.createElement('a');
    downloadAnchorNode.setAttribute('href', dataStr);
    downloadAnchorNode.setAttribute('download', surveyData.name + '.json');
    document.body.appendChild(downloadAnchorNode); // required for firefox
    downloadAnchorNode.click();
    downloadAnchorNode.remove();
  };

  const rows = surveys
    ?.map((survey) => {
      const {
        id,
        name,
        surveyorName,
        siteName,
        buildingName,
        status,
        assets,
        partials,
        flagged,
        lastUpdated,
        surveyType,
        uploadsNotFound,
        uploadsLost,
        uploadsOnOtherDevice,
        uploadsQueueSize,
        surveyTags,
        surveyorId,
        surveyors,
      } = survey;

      return {
        id,
        name,
        surveyorName,
        siteName,
        buildingName,
        status,
        assets,
        partials,
        flagged,
        lastUpdated,
        type: surveyType,
        //don't show uploadsNotFound or uploadsLost for non Asseticom Admins
        uploadsNotFound: IS_ASSETICOM_ADMIN ? uploadsNotFound : 0,
        uploadsLost: IS_ASSETICOM_ADMIN ? uploadsLost : 0,
        uploadsOnOtherDevice,
        uploadsQueueSize: uploadsQueueSize || 0,
        surveyTags,
        surveyorId,
        surveyors,
      };
    })
    .filter((s) => {
      if (IS_QUALITY_ASSURANCE) {
        return s.status === Statuses.COMPLETED;
      }
      return true;
    });

  const showMissingUploadsDialog = ({ rowData }) => {
    setMissingUploadsData(rowData);
  };

  const closeMissingUploadsDialog = () => {
    setMissingUploadsData(undefined);
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell }) => (
          <SurveyNameTableCell
            cell={cell}
            onClickToolTip={(rowData) => {
              showMissingUploadsDialog({ rowData });
            }}
          />
        ),
      },
      { accessorKey: 'type', header: 'Type' },

      {
        accessorKey: 'status',
        filterFn: (row, id, filterValue) => {
          return (
            statusToString(row.original.status)?.toLowerCase() ?? ''
          ).includes(filterValue);
        },
        header: 'Status',
        Cell: StatusStringTableCell,
      },
      {
        accessorKey: 'tags',
        filterFn: (row, id, filterValue) => {
          const tagsString = row.original.surveyTags
            ?.map((tag: SurveyTag) => tag.label)
            .join(' ')
            .toLowerCase();

          if (!tagsString) {
            return false;
          }

          return tagsString.includes(filterValue?.toLowerCase());
        },
        header: 'Tags',
        Cell: TagsTableCell,
      },
      {
        accessorKey: 'surveyorName',
        header: CAN_VIEW_MULTIPLE_SURVEYORS ? 'Surveyors' : 'Surveyor',
        Cell: ({ cell }) => (
          <SurveyorNamesTableCell
            cell={cell}
            showAdditionalSurveyors={CAN_VIEW_MULTIPLE_SURVEYORS}
          />
        ),
        filterFn: (row, id, filterValue) => {
          const fullString = getSurveyorSearchText({
            surveyorName: row.original.surveyorName,
            surveyors: row.original.surveyors,
            mainSurveyorId: row.original.surveyorId,
            includeAdditionalSurveyors: CAN_VIEW_MULTIPLE_SURVEYORS,
          });

          return fullString.includes(filterValue.toLowerCase());
        },
      },
      {
        accessorKey: 'lastUpdated.time',
        header: 'Last Updated',
        Cell: LastUpdatedTableCell,
      },
      { accessorKey: 'siteName', header: 'Site' },
      { accessorKey: 'buildingName', header: 'Building' },
      {
        accessorKey: 'assets',
        header: 'Assets',
        ...RIGHT_ALIGN_HEADER,
        ...RIGHT_ALIGN_BODY,
      },
      {
        accessorKey: 'partials',
        header: 'Partials',
        ...RIGHT_ALIGN_HEADER,
        ...RIGHT_ALIGN_BODY,
      },
      {
        accessorKey: 'flagged',
        header: 'Flagged',
        ...RIGHT_ALIGN_HEADER,
        ...RIGHT_ALIGN_BODY,
      },
      {
        accessorKey: 'uploadsQueueSize',
        header: 'Queued Uploads',
        ...RIGHT_ALIGN_HEADER,
        ...RIGHT_ALIGN_BODY,
      },
    ],
    [CAN_VIEW_MULTIPLE_SURVEYORS],
  );

  const onClickedEditSurvey = (surveyId) => {
    history.push(`${location.pathname}/${surveyId}`);
  };

  const onClickedViewSurvey = ({ id }) => {
    const survey = surveys.find((s) => s.id === id);
    if (
      (survey?.status !== statuses.NOT_STARTED &&
        survey?.status !== statuses.ISSUED) ||
      canEditSurveys
    ) {
      history.push(`${location.pathname}/${id}`);
    }
  };

  const onClickReissueSurvey = async ({ id }) => {
    history.push(`${location.pathname}/reissue/${id}`);
  };

  const actionButtons = useCallback(
    ({ row, closeMenu }) => {
      const buttons: JSX.Element[] = [];

      const rowData = row.original;

      if (
        rowData.status === statuses.IN_PROGRESS ||
        rowData.status === statuses.COMPLETED
      ) {
        buttons.push(
          <TableActionButton
            key="View"
            text="View"
            colour="primary"
            onClick={() => closeMenu(onClickedViewSurvey(rowData))}
          />,
        );
      }

      if (
        userClaims?.roles?.asseticom >= ALL_ROLES.LEAD_SURVEYOR ||
        (clientId && userClaims?.roles?.[clientId] >= ALL_ROLES.LEAD_SURVEYOR)
      ) {
        buttons.push(
          <TableActionButton
            key="Duplicate"
            text="Duplicate"
            colour="secondary"
            onClick={() => closeMenu(onClickDuplicateSurvey(rowData.id))}
          />,
        );
      }

      if (
        canShowSurveySetupEditAction({
          roles: userClaims?.roles,
          clientId,
          status: rowData.status,
        })
      ) {
        buttons.push(
          <TableActionButton
            key="Edit"
            text="Edit"
            colour="blue"
            onClick={() => closeMenu(onClickedEditSurvey(rowData.id))}
          />,
        );
      }

      if (
        userClaims?.roles?.asseticom >= ALL_ROLES.LEAD_SURVEYOR ||
        (clientId && userClaims?.roles?.[clientId] >= ALL_ROLES.LEAD_SURVEYOR)
      ) {
        buttons.push(
          <TableActionButton
            key="Reissue"
            text="Reissue"
            colour="secondary"
            onClick={() => closeMenu(onClickReissueSurvey(rowData))}
          />,
        );
      }
      if (userClaims?.roles?.asseticom >= ALL_ROLES.LEAD_SURVEYOR) {
        buttons.push(
          <TableActionButton
            key="Download"
            text="Download"
            colour="blue"
            onClick={() => closeMenu(onClickDownloadSurvey(rowData))}
          />,
        );
      }
      if (
        rowData.status !== statuses.IN_PROGRESS &&
        (userClaims?.roles?.asseticom >= ALL_ROLES.LEAD_SURVEYOR ||
          (clientId &&
            userClaims?.roles?.[clientId] >= ALL_ROLES.LEAD_SURVEYOR))
      ) {
        buttons.push(
          <TableActionButton
            key="Delete"
            text="Delete"
            colour="red"
            onClick={() => closeMenu(showConfirmDialogForSurvey(rowData))}
          />,
        );
      }
      if (
        rowData.status !== statuses.COMPLETED &&
        userClaims?.roles?.asseticom >= ALL_ROLES.ASSETICOM_ADMIN
      ) {
        buttons.push(
          <TableActionButton
            key="Mark as Complete"
            text="Mark as Complete"
            colour="green"
            onClick={() => closeMenu(markSurveyAsComplete(rowData))}
          />,
        );
      }
      return (
        <Stack direction="column" p={1}>
          {buttons}
        </Stack>
      );
    },
    [userClaims, clientId],
  );

  const onClickExportSurveyList = async () => {
    const allSites = await firestore
      .collection('clients')
      .doc(clientId)
      .collection('sites')
      .get();

    const allAssetLists = await firestore
      .collection('clients')
      .doc(clientId)
      .collection('assetLists')
      .get();

    const allAssetListsById = {};

    allAssetLists.docs.forEach((assetListDoc) => {
      allAssetListsById[assetListDoc.id] = assetListDoc.data();
    });

    const sitesById = {};

    allSites.docs.forEach((siteDoc) => {
      const siteData = siteDoc.data();
      const siteId = siteDoc.id;
      sitesById[siteId] = siteData;
    });

    const allSurveysMetaSnapshot = await firestore
      .collection('clients')
      .doc(clientId)
      .collection('surveys-meta')
      .get();

    const allSurveysMeta = parseSurveysMeta(
      allSurveysMetaSnapshot.docs.map((doc) => doc.data()),
    );

    const metaHasAssetListId = allSurveysMeta.list.filter(
      (survey) => survey.assetListId,
    ).length;

    const metaHasSurveyTags = allSurveysMeta.list.filter(
      (survey) => survey.surveyTags && survey.surveyTags.length > 0,
    ).length;

    const allSurveysStatusHistorySnapshot = await firestore
      .collection('clients')
      .doc(clientId)
      .collection('surveys-status-history')
      .get();

    const allSurveysHistory = parseSurveyHistories(
      allSurveysStatusHistorySnapshot.docs.map((doc) => doc.data()),
    );

    if (!allSurveysMeta.list.length) {
      return;
    }

    const surveysData = allSurveysMeta.list.sort(
      (a, b) => a.lastUpdated.time - b.lastUpdated.time,
    );

    const workbook = XLSX.utils.book_new();

    const SHOW_COLUMNS = [
      'Name',
      'Type',
      'Status',
      metaHasSurveyTags ? 'Tags' : '',
      metaHasAssetListId ? 'Taxonomy' : '',
      'Surveyor',
      'Last Updated',
      'Site Name',
      'Building Name',
      'GIFA (m2)',
      'External Area (m2)',
      'City/Town',
      'County',
      'Post Code',
      'Region',
      'Assets',
      'Partials',
      'Flagged',
      'Estimated Start Date',
      'Actual Start Date',
      'Estimated Days to Complete',
      'Actual Days to Complete',
    ].filter(Boolean);

    const ALL_COLUMNS = getStyledExportHeaders(SHOW_COLUMNS);

    const surveyRows = surveysData.map((survey) => {
      const siteForSurvey: SiteProps = sitesById[survey.siteId];
      const statusesForSurvey = allSurveysHistory?.[survey.id];

      let actualStartDate = [Statuses.NOT_STARTED, Statuses.ISSUED].includes(
        survey.status,
      )
        ? null
        : survey.created;

      const hasStartDate = statusesForSurvey?.find(
        (statusForSurvey) => statusForSurvey.status === Statuses.IN_PROGRESS,
      );
      if (hasStartDate) {
        actualStartDate = hasStartDate.time;
      }

      let actualEndDate = survey.created;
      const hasEndDate = statusesForSurvey?.find(
        (statusForSurvey) => statusForSurvey.status === Statuses.COMPLETED,
      );

      if (hasEndDate) {
        actualEndDate = hasEndDate.time;
      }

      let actualDuration = survey.status === Statuses.COMPLETED ? 1 : null;

      const ONE_DAY_IN_MILLISECONDS = 86400000;

      if (hasStartDate && hasEndDate) {
        actualDuration = Math.round(
          (hasEndDate.time - hasStartDate.time) / ONE_DAY_IN_MILLISECONDS,
        );
      }

      return [
        {
          v: survey.name,
          t: 's',
        },
        {
          v: survey.surveyType || '',
          t: 's',
        },
        {
          v: statusToString(survey.status),
          t: 's',
        },
        metaHasSurveyTags
          ? {
              v: survey.surveyTags?.map((tag) => tag.label).join(', ') || '',
              t: 's',
            }
          : undefined,
        metaHasAssetListId
          ? {
              v: allAssetListsById[survey.assetListId]?.name || '',
              t: 's',
            }
          : undefined,
        {
          v: survey.surveyorName || '',
          t: 's',
        },
        {
          v: `${dateFromTimeStamp(survey.lastUpdated.time)} ${timeFromTimeStamp(
            survey.lastUpdated.time,
          )}`,
          t: 's',
        },
        {
          v: survey.siteName,
          t: 's',
        },
        {
          v: survey.buildingName,
          t: 's',
        },
        {
          v: survey.gifa,
          t: 'n',
        },
        {
          v: survey.external || 0,
          t: 'n',
        },
        {
          v: siteForSurvey.townCity,
          t: 's',
        },
        {
          v: siteForSurvey.county,
          t: 's',
        },
        {
          v: siteForSurvey.postCode,
          t: 's',
        },
        {
          v: siteForSurvey.region || 'N/A',
          t: 's',
        },
        {
          v: survey.assets ?? 0 ?? 0,
          t: 'n',
        },
        {
          v: survey.partials ?? 0 ?? 0,
          t: 'n',
        },
        {
          v: survey.flagged ?? 0,
          t: 'n',
        },
        {
          v: dateFromTimeStamp(survey.estimatedStartDate || survey.created),
          t: 'd',
        },
        {
          v: actualStartDate
            ? dateFromTimeStamp(actualStartDate)
            : NOT_APPLICABLE,
          t: actualStartDate ? 'd' : 's',
          s: { alignment: { horizontal: 'right' } },
        },
        {
          v: survey.estimatedDuration || 1,
          t: 'n',
        },
        {
          v: actualDuration || NOT_APPLICABLE,
          t: actualDuration ? 'n' : 's',
          s: { alignment: { horizontal: 'right' } },
        },
      ].filter(Boolean);
    });

    const sRowStyled = getStyledRows(surveyRows);

    const worksheet = XLSX.utils.aoa_to_sheet([ALL_COLUMNS, ...sRowStyled]);
    XLSX.utils.sheet_add_aoa(worksheet, [ALL_COLUMNS], {
      origin: 'A1',
    });

    const fixedWorksheet = await autoSizeWorksheetWidth(worksheet);
    const workbookName = getSafeExcelSheetName(
      `${client.name.substring(0, 23)} Surveys`,
    );

    XLSX.utils.book_append_sheet(workbook, fixedWorksheet, workbookName);
    XLSX.writeFile(workbook, `${workbookName}.xlsx`);
  };

  function updateSurveyTags({ id, tags }) {
    if (!clientId) {
      return;
    }
    // const surveyMetaDoc = firestore
    //   .collection('clients')
    //   .doc(clientId)
    //   .collection('surveys-meta')
    //   .doc(`surveys_${ id?.charAt(0) } `);

    // surveyMetaDoc.update({
    //   [`${ id }.tags`]: tags,
    // });

    const surveyDoc = firestore
      .collection('clients')
      .doc(clientId)
      .collection('surveys')
      .doc(id);

    surveyDoc.update({
      surveyTags: tags,
      ...getLastUpdated(),
    });
  }
  function handleTagAdded(tag: SurveyTag) {
    if (!clientId) {
      return;
    }

    // Use the firestore instance directly without going through Redux, stops weird redux serialize warning
    const db = firebase.firestore();

    const clientDocRef = db.collection('clients').doc(clientId);

    return db.runTransaction(async (transaction) => {
      transaction.update(clientDocRef, {
        surveyTags: firestore.FieldValue.arrayUnion(tag),
      });
    });
  }

  function handleTagDeleted(tag: SurveyTag) {
    if (!clientId) {
      return;
    }

    // Use the firestore instance directly without going through Redux, stops weird redux serialize warning
    const db = firebase.firestore();
    const clientDocRef = db.collection('clients').doc(clientId);

    return db.runTransaction(async (transaction) => {
      const clientDoc = await transaction.get(clientDocRef);
      const existingTags: SurveyTag[] = clientDoc?.data()?.surveyTags || [];
      const updatedTags = existingTags.filter((t) => t.id !== tag.id);
      transaction.update(clientDocRef, { surveyTags: updatedTags });
    });
  }

  if (!surveys.length) {
    return null;
  }

  return (
    <Paper sx={{ width: '100%' }} id="main-content" elevation={0}>
      <ErrorBoundary>
        <TagsSelectorProvider
          onSave={updateSurveyTags}
          onTagAdded={handleTagAdded}
          onTagDeleted={handleTagDeleted}
        >
          <TagsSelectorDialog />
          <Dialog
            fullScreen={false}
            open={!!missingUploadsData}
            onClose={closeMissingUploadsDialog}
            aria-labelledby="missing-uploads-dialog-title"
            aria-describedby="missing-uploads-dialog-description"
            sx={{ backgroundColor: 'transparent' }}
          >
            {missingUploadsData ? (
              <>
                <DialogTitle id="missing-uploads-dialog-title" align="center">
                  Missing Uploads
                </DialogTitle>
                <DialogContent>
                  <MissingUploadsContent
                    clientId={clientId}
                    surveyId={missingUploadsData?.id}
                  />
                </DialogContent>
                <DialogActions>
                  <Button onClick={closeMissingUploadsDialog} color={'primary'}>
                    OK
                  </Button>
                </DialogActions>
              </>
            ) : null}
          </Dialog>
          <Table
            rows={rows}
            columns={columns}
            onClickRow={onClickedViewSurvey}
            sortBy={{ id: 'lastUpdated.time', desc: true }}
            tableProps={{
              stateStorageKey: `surveys-list-${clientId || 'default'}`,
              //@ts-ignore
              renderRowActionMenuItems: actionButtons,
              enableRowActions: !IS_VIEWER,
              positionActionsColumn: 'first',
              renderTopToolbarCustomActions: () => {
                return (
                  <Stack
                    px={1}
                    mb={1}
                    direction="row"
                    justifyContent="flex-end"
                    sx={{ width: '100%' }}
                  >
                    <Button
                      variant="outlined"
                      onClick={onClickExportSurveyList}
                      color="secondary"
                    >
                      Export Survey List
                    </Button>
                  </Stack>
                );
              },
            }}
          />
        </TagsSelectorProvider>
      </ErrorBoundary>
    </Paper>
  );
};

export default View;
